import { drop, take } from '@execonline-inc/collections';
import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { array } from 'jsonous';
import { fromEmpty } from 'maybeasy';
import { observer } from 'mobx-react';
import { emptyFragment } from '../../../../../EmptyFragment';
import { resourceDecoder } from '../../../../../Resource/Decoders';
import { T } from '../../../../../Translations';
import { useQueryWithDecoder } from '../../../../../UseQueryWithDecoder';
import { attributeResourceDecoder } from '../../../Decoders';
import ExcessCompetencies from '../../../LearningCollection/ExcessCompetencies';
import { LearningCollectionSummaryResource } from '../../../Types';
import CompetencyLabel from './CompetencyLabel';

interface Props {
  learningCollectionSummaryResource: LearningCollectionSummaryResource;
}
const previewAmount = 5;

export const competenciesResourceDecoder = resourceDecoder(array(attributeResourceDecoder));

function Competencies({ learningCollectionSummaryResource }: Props) {
  const { data, isLoading, error } = useQueryWithDecoder(
    'competencies',
    learningCollectionSummaryResource.links,
    competenciesResourceDecoder,
  );

  if (isLoading) {
    return (
      <div className="basis-1/3 animate-pulse">
        <div className="mb-2 h-4 w-16 bg-gray-300 rounded" />
        <div className="flex my-2 gap-x-2 gap-y-2 flex-wrap">
          {[...Array(9)].map((_, index) => (
            <div key={index} className="bg-gray-300 rounded-md h-6 w-20" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    warnAndNotify('competencies-load-failure', 'Failed to load competencies:', error || {});
    return emptyFragment();
  }

  return (
    <div className="basis-1/3">
      {fromEmpty(data.payload)
        .map((competencies) => (
          <>
            <h3 className="mb-2 text-sm font-semibold text-gray-500">
              <T kind="Competencies" />
            </h3>
            <div className="flex my-2 gap-x-2 gap-y-2 flex-wrap w-2/3">
              {take(previewAmount, competencies).map((competency) => (
                <CompetencyLabel competency={competency} key={competency.payload.id} />
              ))}
              <ExcessCompetencies competencies={drop(previewAmount, competencies)} />
            </div>
          </>
        ))
        .getOrElse(emptyFragment)}
    </div>
  );
}

export default observer(Competencies);
